import React, { createContext, useContext, useRef, useState, useId } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import styles from './Form.module.css';

// ── Types ────────────────────────────────────────────────────────────────────

type FormLayout = 'horizontal' | 'vertical' | 'inline';

interface FormRule {
  required?: boolean;
  message?: string;
  min?: number;
  max?: number;
  pattern?: RegExp;
  validator?: (value: unknown) => Promise<void> | void;
}

interface FormInstance {
  getFieldValue: (name: string) => unknown;
  getFieldsValue: () => Record<string, unknown>;
  setFieldValue: (name: string, value: unknown) => void;
  setFieldsValue: (values: Record<string, unknown>) => void;
  validateFields: () => Promise<Record<string, unknown>>;
  resetFields: () => void;
  getFieldError: (name: string) => string[];
}

/** @internal Extended form instance with internal methods */
interface FormInstanceInternal extends FormInstance {
  __registerRules: (name: string, rules: FormRule[]) => void;
  __valuesRef: React.MutableRefObject<Record<string, unknown>>;
  __errorsRef: React.MutableRefObject<Record<string, string[]>>;
}

// ── CVA definitions ──────────────────────────────────────────────────────────

const form = cva(styles.form, {
  variants: {
    layout: {
      vertical: styles.formVertical,
      horizontal: styles.formHorizontal,
      inline: styles.formInline,
    },
  },
  defaultVariants: {
    layout: 'vertical',
  },
});

const formItem = cva(styles.item, {
  variants: {
    layout: {
      vertical: styles.itemVertical,
      horizontal: styles.itemHorizontal,
      inline: styles.itemInline,
    },
  },
  defaultVariants: {
    layout: 'vertical',
  },
});

// ── useForm hook ─────────────────────────────────────────────────────────────

/**
 * useForm — create a form instance for managing field values and validation.
 *
 * @example
 * const [form] = Form.useForm();
 * <Form form={form} onFinish={handleSubmit}>
 *   <Form.Item name="email" rules={[{ required: true }]}>
 *     <Input />
 *   </Form.Item>
 * </Form>
 */
export function useForm(): [FormInstance] {
  const valuesRef = useRef<Record<string, unknown>>({});
  const errorsRef = useRef<Record<string, string[]>>({});
  const rulesRef = useRef<Record<string, FormRule[]>>({});
  const [, forceUpdate] = useState(0);

  const instance: FormInstance = {
    getFieldValue: (name) => valuesRef.current[name],
    getFieldsValue: () => ({ ...valuesRef.current }),
    setFieldValue: (name, value) => {
      valuesRef.current[name] = value;
      forceUpdate((n) => n + 1);
    },
    setFieldsValue: (values) => {
      Object.assign(valuesRef.current, values);
      forceUpdate((n) => n + 1);
    },
    validateFields: async () => {
      const errors: Record<string, string[]> = {};
      for (const [name, rules] of Object.entries(rulesRef.current)) {
        const value = valuesRef.current[name];
        const fieldErrors: string[] = [];
        for (const rule of rules) {
          if (rule.required && (value === undefined || value === null || value === '')) {
            fieldErrors.push(rule.message ?? `${name} is required`);
          }
          if (rule.min !== undefined && typeof value === 'string' && value.length < rule.min) {
            fieldErrors.push(rule.message ?? `${name} must be at least ${rule.min} characters`);
          }
          if (rule.max !== undefined && typeof value === 'string' && value.length > rule.max) {
            fieldErrors.push(rule.message ?? `${name} must be at most ${rule.max} characters`);
          }
          if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
            fieldErrors.push(rule.message ?? `${name} is invalid`);
          }
          if (rule.validator) {
            try {
              await rule.validator(value);
            } catch (e: unknown) {
              fieldErrors.push((e instanceof Error ? e.message : null) ?? String(e));
            }
          }
        }
        if (fieldErrors.length > 0) errors[name] = fieldErrors;
      }
      errorsRef.current = errors;
      forceUpdate((n) => n + 1);
      if (Object.keys(errors).length > 0) throw errors;
      return { ...valuesRef.current };
    },
    resetFields: () => {
      valuesRef.current = {};
      errorsRef.current = {};
      forceUpdate((n) => n + 1);
    },
    getFieldError: (name) => errorsRef.current[name] ?? [],
  };

  // Register rules from Form.Item
  (instance as FormInstanceInternal).__registerRules = (name: string, rules: FormRule[]) => {
    rulesRef.current[name] = rules;
  };

  // Get/set refs for internal use
  (instance as FormInstanceInternal).__valuesRef = valuesRef;
  (instance as FormInstanceInternal).__errorsRef = errorsRef;

  return [instance];
}

// ── Context ──────────────────────────────────────────────────────────────────

interface FormContextValue {
  form: FormInstance;
  layout: FormLayout;
}

const FormContext = createContext<FormContextValue | null>(null);

// ── Form ─────────────────────────────────────────────────────────────────────

interface FormProps extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  form?: FormInstance;
  layout?: FormLayout;
  onFinish?: (values: Record<string, unknown>) => void;
  onFinishFailed?: (errors: Record<string, string[]>) => void;
  initialValues?: Record<string, unknown>;
  children?: React.ReactNode;
}

/**
 * Form — form layout with validation.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | form | FormInstance | auto-created | Form instance from useForm |
 * | layout | 'horizontal' \| 'vertical' \| 'inline' | 'vertical' | Layout |
 * | onFinish | (values) => void | — | Submit success handler |
 * | onFinishFailed | (errors) => void | — | Validation failure handler |
 * | initialValues | Record<string, unknown> | — | Initial field values |
 *
 * Sub: Form.Item, Form.useForm
 *
 * @example
 * <Form onFinish={console.log}>
 *   <Form.Item name="email" label="Email" rules={[{ required: true }]}>
 *     <Input />
 *   </Form.Item>
 *   <Button htmlType="submit" variant="primary">Submit</Button>
 * </Form>
 */
export function Form({
  form: externalForm,
  layout = 'vertical',
  onFinish,
  onFinishFailed,
  initialValues,
  children,
  className,
  style,
  ...props
}: FormProps) {
  const [internalForm] = useForm();
  const activeForm = externalForm ?? internalForm;

  // Set initial values (only on mount)
  const formRef = React.useRef(activeForm);
  formRef.current = activeForm;
  const initialValuesRef = React.useRef(initialValues);
  initialValuesRef.current = initialValues;
  React.useEffect(() => {
    if (initialValuesRef.current) formRef.current.setFieldsValue(initialValuesRef.current);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const values = await activeForm.validateFields();
      onFinish?.(values);
    } catch (errors) {
      onFinishFailed?.(errors as Record<string, string[]>);
    }
  };

  return (
    <FormContext.Provider value={{ form: activeForm, layout }}>
      <form
        onSubmit={handleSubmit}
        className={clsx(form({ layout }), className)}
        style={style}
        {...props}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}

// ── Form.Item ────────────────────────────────────────────────────────────────

interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {
  name?: string;
  label?: React.ReactNode;
  rules?: FormRule[];
  validateTrigger?: 'onChange' | 'onBlur';
  children?: React.ReactElement;
  required?: boolean;
}

/**
 * Form.Item — wraps a form control with label, validation, and error display.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | name | string | — | Field name |
 * | label | ReactNode | — | Label text |
 * | rules | FormRule[] | — | Validation rules |
 * | required | boolean | — | Show required asterisk |
 * | validateTrigger | 'onChange' \| 'onBlur' | 'onChange' | When to validate |
 */
function FormItem({
  name,
  label,
  rules,
  validateTrigger: _validateTrigger = 'onChange',
  required,
  children,
  className,
  style,
  ...props
}: FormItemProps) {
  const ctx = useContext(FormContext);
  const activeForm = ctx?.form;
  const layout = ctx?.layout ?? 'vertical';
  const inputId = useId();

  // Register rules
  React.useEffect(() => {
    if (name && rules && activeForm) {
      (activeForm as FormInstanceInternal).__registerRules?.(name, rules);
    }
  }, [name, rules, activeForm]);

  const isRequired = required ?? rules?.some((r) => r.required);
  const errors = name ? (activeForm?.getFieldError(name) ?? []) : [];
  const value = name ? activeForm?.getFieldValue(name) : undefined;

  const childProps: Record<string, unknown> = { id: inputId };
  if (isRequired) {
    childProps['aria-required'] = 'true';
  }
  if (name && activeForm) {
    childProps.value = value ?? '';
    childProps.onChange = (valOrEvent: unknown) => {
      const isEvent = typeof valOrEvent === 'object' && valOrEvent !== null && 'target' in valOrEvent;
      const v = isEvent ? (valOrEvent as React.ChangeEvent<HTMLInputElement>).target.value : valOrEvent;
      activeForm.setFieldValue(name, v);
    };
    if (errors.length > 0) {
      childProps.status = 'error';
    }
  }

  return (
    <div
      className={clsx(formItem({ layout }), className)}
      style={style}
      {...props}
    >
      {label && (
        <label
          htmlFor={inputId}
          className={clsx(styles.label, layout === 'horizontal' && styles.labelHorizontal)}
        >
          {isRequired && <span className={styles.required}>*</span>}
          {label}
        </label>
      )}
      <div className={styles.fieldContent}>
        {children && React.cloneElement(children, childProps)}
        {errors.length > 0 && (
          <div
            role="alert"
            className={styles.errorMessage}
          >
            {errors[0]}
          </div>
        )}
      </div>
    </div>
  );
}

Form.Item = FormItem;
Form.useForm = useForm;
